import { Injectable } from '@angular/core';
import { ENVIRONMENT } from '../../environments/environment';
import CONFIGURATION from '../../configurations/configuration';
import { ReplaySubject, Observable } from 'rxjs';
import { initialize, LDClient, LDFlagSet } from 'launchdarkly-js-client-sdk';
import { AuthService } from './auth.service';
import { MessageService } from './message.service';
import { MemberService } from './member.service';
import { LanguageService } from './language.service';
import { take } from 'rxjs/operators';

/* The following code can be copied to another component to integrate with the
 feature flag.

    const { ctos } = this.featureFlagService.getFeatureFlagKeys();
    this.featureFlagObservable = this.featureFlagService.getFeatureFlagObservable();
    this.featureFlagObservable.subscribe( (flags) => {
      this.enableCtos = flags[ctos];
    });
*/


@Injectable()
export class FeatureFlagService {
  ldClient: LDClient;
  flags: LDFlagSet;
  flagChangedMessage: ReplaySubject<LDFlagSet> = new ReplaySubject();
  messageServiceObservable: Observable<any>;
  enableFlagDebug: Boolean = false;
  featureFlagKeys: any;

  constructor(
    private authService: AuthService,
    private languageService: LanguageService,
    private messageService: MessageService,
    private memberService: MemberService
  ) {
    this.featureFlagKeys = Object.freeze({
      afpiLogo: 'afpi-logo',
      homepageBannerCampaign: 'homepage-banner-campaign',
      ojkStatistic: 'ojk-statistic',
      intercomFlag: 'intercom',
      idLocalPerformance: 'id-local-performance',
      sgLocalPerformance: 'sg-local-performance',
      myLocalPerformance: 'my-local-performance',
      twoFa: 'two-fa',
      fpx: 'fpx',
      partialBannerFlag: 'partial-banner-flag',
      yearInReview: 'year-in-review',
      sgOneMapAutoPopulate: 'sg-one-map-auto-populate',
      idFormStackBorrower: 'id-form-stack-borrower',
      bniInstructionEnable: 'bni-instruction-enable',
      whtCertificateEnable: 'wht-certificate-enable',
      loginPageBanner: 'login-page-banner',
      webPushNotification: 'web-push-notification',
      enablePrivyConsent: 'enable-privy-consent',
      enableConsolidatedVans: 'enable-consolidated-vans',
      enableEsignPerInvestment: 'enable-esign-per-investment',
      sgSatQuestionsV2: 'sg-sat-questions-v2',
      enableRecaptcha: 'enable-recaptcha',
      maintenanceMode: 'investor-borrower-web-app-maintenance-mode-per-country',
      enableTurnstile: 'investor-webapp-enable-turnstile',
    });
    this.flags = {};
    this.messageServiceObservable = this.messageService.getLoginSubject();
    this.messageServiceObservable.subscribe((event) => {
      if (event === 'initializeLdClient') {
        this.initializeLdClient();
      }
    });
  }

  async getContext() {
    const username = this.authService.getUserName();
    const countryCode = CONFIGURATION.country_code;
    if (username) {
      return new Promise<any>(resolve => {
        this.memberService.getUserHashCode().pipe(take(1))
          .subscribe(
            (response: any) => {
              let result;
              const userHashCode = response.data.hashcode;
              if (!countryCode) {
                return false;
              }
              result = {
                kind: 'user',
                key: userHashCode || 'anonymous.user@fs.com',
                email: username || 'anonymous.user@fs.com',
                country: countryCode,
                os: 'web',
                language: this.languageService.getDefaultLanguage(),
                memberType: this.authService.getMemberTypeCode(),
                _meta: {
                  privateAttributes: ['email'],
                }
              }
              resolve(result);
            },
            (_) => {
              resolve(this.defaultUser);
            });
      })
    } else {
      return this.defaultUser;
    }
  }

  async initializeLdClient() {
    const context = await this.getContext();
    if (!context) {
      if (this.enableFlagDebug) {
        console.log('No context is found');
      }
      return;
    }
    this.ldClient = initialize(ENVIRONMENT.launch_darkly_client_id, context, {
      bootstrap: 'localStorage'
    });
    this.ldClient.on('ready', () => {
      this.setFlags(this.ldClient.allFlags());
    });
    this.ldClient.on('change', (flags) => {
      this.setFlags(flags, true);
    });
  }

  setFlags(flags, change = false) {
    if (change) {
      const changes = {};
      Object.entries(flags).forEach(([key, value]) => {
        changes[key] = value['current'];
      });
      this.flags = Object.assign(this.flags, changes);
    } else {
      this.flags = Object.assign(this.flags, flags);
    }
    if (this.enableFlagDebug) {
      console.log('change -> flags: \n', JSON.stringify(this.flags, null, 2));
    }
    this.flagChangedMessage.next(this.flags);
  }

  getFeatureFlagKeys() {
    return this.featureFlagKeys;
  }

  getFeatureFlagObservable() {
    return this.flagChangedMessage.asObservable();
  }

  closeLdClient() {
    if (this.ldClient) {
      this.ldClient.close();
    }
  }

  get defaultUser() {
    return {
      kind: 'user',
      key: 'anonymous.user@fs.com',
      email: 'anonymous.user@fs.com',
      country: CONFIGURATION.country_code,
      os: 'web',
      language: this.languageService.getDefaultLanguage(),
      memberType: this.authService.getMemberTypeCode(),
      _meta: {
        privateAttributes: ['email'],
      }
    }
  }
}
